import dayjs from 'dayjs';

export function dateTime(ms: number | null | undefined): string {
  return ms ? dayjs(ms).format('D.M.YYYY HH:mm:ss') : '—';
}

/** Short form for tables: time only for today, otherwise date and time. */
export function shortTime(ms: number): string {
  const d = dayjs(ms);
  return d.isSame(dayjs(), 'day') ? d.format('HH:mm:ss') : d.format('D.M. HH:mm:ss');
}

export function ago(ms: number | null | undefined): string {
  if (!ms) return 'never';
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export function count(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : n.toLocaleString('fi-FI');
}

export function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)} h`;
  return `${(ms / 86_400_000).toFixed(1)} d`;
}

/** Quotes a value for the query syntax when it contains spaces or quotes. */
export function quote(v: string): string {
  return /[\s"]/.test(v) ? `"${v.replace(/"/g, '')}"` : v;
}

/** Pretty prints XML or JSON payloads for display; other text is returned as is. */
export function prettyPayload(text: string, type: string): string {
  if (type === 'json') {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }
  if (type === 'xml' && !text.includes('\n<')) {
    let depth = 0;
    return text.replace(/>\s*</g, '><').replace(/(<\/?[^>]+>)/g, (tag) => {
      if (tag.startsWith('</')) depth--;
      const line = '\n' + '  '.repeat(Math.max(0, depth)) + tag;
      if (!tag.startsWith('</') && !tag.endsWith('/>') && !tag.startsWith('<?') && !tag.startsWith('<!')) depth++;
      return line;
    }).replace(/>([^<\n]+)\n\s*<\//g, '>$1</').trim();
  }
  return text;
}

/** Compact time for lists: HH:mm today, otherwise D.M. HH:mm. */
export function compactTime(ms: number): string {
  const d = dayjs(ms);
  return d.isSame(dayjs(), 'day') ? d.format('HH:mm') : d.format('D.M. HH:mm');
}

/** A field name for people: InvoiceTotalVatIncludedAmount → Invoice total vat included amount. */
export function humanize(name: string): string {
  if (/\s/.test(name) || name.length <= 3) return name;
  const s = name.replace(/^@/, '').replace(/__c$/i, '').replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/ ([A-Z])(?=[a-z])/g, (_m, c: string) => ' ' + c.toLowerCase());
}

/** a or an, by the first letter. */
export const article = (w: string) => (/^[aeiou]/i.test(w) ? 'an' : 'a');
